// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Badge, Box, Button, Card, Center, Container, PasswordInput, Stack, Text, Title } from '@/components/ui';
import { IconLock, IconArrowLeft, IconLockCheck, IconSparkles, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn, ScaleIn, HoverLift, useReducedMotion } from '@/components/animations';
import { AnimatedFormWrapper, PasswordStrength } from '../components';

export default function ResetAnimated() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isReset, setIsReset] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsReset(true);
  };

  const passwordsMatch = password === confirmPassword;
  const canSubmit = password.length >= 8 && passwordsMatch;

  if (isReset) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: isDark
            ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
            : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.green[0]} 100%)`,
        }}
      >
        <Center mih="100vh" p="md">
          <Container size="xs" w="100%">
            <ScaleIn delay={100}>
              <Card variant="elevated" p="xl" radius="lg">
                <Stack gap="lg" align="center">
                  <ScaleIn delay={200}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'var(--mantine-color-green-light)',
                      }}
                    >
                      <IconCheck size={40} color="var(--mantine-color-green-6)" />
                    </Center>
                  </ScaleIn>

                  <FadeIn delay={300}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center">Password Reset!</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        Your password has been successfully reset.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <SlideIn direction="up" delay={400}>
                    <Button
                      component={Link}
                      to="/demo/auth-pages/login/animated"
                      fullWidth
                      size="md"
                      radius="md"
                      variant="gradient"
                      gradient={{ from: 'green', to: 'teal' }}
                    >
                      Continue to login
                    </Button>
                  </SlideIn>
                </Stack>
              </Card>
            </ScaleIn>
          </Container>
        </Center>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.green[0]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 80 + i * 40,
              height: 80 + i * 40,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.green[isDark ? 8 : 3]}, ${theme.colors.teal[isDark ? 8 : 3]})`,
              opacity: 0.1,
              top: `${20 + i * 15}%`,
              right: `${5 + i * 15}%`,
              animation: reduceMotion ? 'none' : `float ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </Box>

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <ScaleIn delay={100} duration={500}>
            <HoverLift>
              <Card variant="elevated" p="xl" radius="lg">
                <Stack gap="lg">
                  <SlideIn direction="left" delay={200}>
                    <Button
                      component={Link}
                      to="/demo/auth-pages/reset-password"
                      variant="subtle"
                      size="xs"
                      leftSection={<IconArrowLeft size={14} />}
                      w="fit-content"
                    >
                      Back to variants
                    </Button>
                  </SlideIn>

                  <FadeIn delay={300}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconSparkles size={12} />}
                        variant="gradient"
                        gradient={{ from: 'green', to: 'teal' }}
                        size="lg"
                      >
                        Animated Experience
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'var(--mantine-color-green-light)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconLockCheck size={28} color="var(--mantine-color-green-6)" />
                      </Center>
                      <Title order={2} ta="center" mt="sm">
                        Set new password
                      </Title>
                      <Text c="dimmed" size="sm" ta="center" maw={300}>
                        Your new password must be different from previous passwords.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={100}>
                      <div>
                        <PasswordInput
                          label="New password"
                          placeholder="Enter new password"
                          leftSection={<IconLock size={16} />}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                        />
                        {password && <PasswordStrength password={password} variant="bar" />}
                      </div>

                      <PasswordInput
                        label="Confirm password"
                        placeholder="Confirm new password"
                        leftSection={<IconLock size={16} />}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        error={confirmPassword && !passwordsMatch ? "Passwords don't match" : undefined}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        variant="gradient"
                        gradient={{ from: 'green', to: 'teal' }}
                        disabled={!canSubmit}
                      >
                        Reset password
                      </Button>
                    </AnimatedFormWrapper>
                  </form>

                  <FadeIn delay={700}>
                    <Center>
                      <Button
                        component={Link}
                        to="/demo/auth-pages/login/animated"
                        variant="subtle"
                        leftSection={<IconArrowLeft size={14} />}
                      >
                        Back to login
                      </Button>
                    </Center>
                  </FadeIn>
                </Stack>
              </Card>
            </HoverLift>
          </ScaleIn>
        </Container>
      </Center>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
}
